package it.neckar.lizergy.model.assemblyPortfolio

import it.neckar.editHistory.PositionEditHistory
import it.neckar.lizergy.model.ElementsSelectionEntry
import it.neckar.lizergy.model.assemblyPortfolio.AssemblyPortfolio.AssemblyPortfolioHeaterRod
import it.neckar.lizergy.model.assemblyPortfolio.AssemblyPortfolio.AssemblyPortfolioModuleLayout
import it.neckar.lizergy.model.assemblyPortfolio.AssemblyPortfolio.AssemblyPortfolioWallbox
import it.neckar.lizergy.model.assemblyPortfolio.AssemblyPortfolio.StringSelection
import it.neckar.lizergy.model.company.CompanyCode
import it.neckar.lizergy.model.company.CompanyResolver
import it.neckar.lizergy.model.company.PlannerCompanyInformation
import it.neckar.lizergy.model.company.UserResolver
import it.neckar.lizergy.model.company.user.UserInformation
import it.neckar.lizergy.model.configuration.components.Einspeiseart
import it.neckar.lizergy.model.configuration.components.FacilityOperatorInformation
import it.neckar.lizergy.model.configuration.components.ScaffoldingArea
import it.neckar.lizergy.model.configuration.components.ScaffoldingProvided
import it.neckar.lizergy.model.configuration.components.SelfAssemblyConfiguration
import it.neckar.lizergy.model.configuration.moduleLayout.ModuleLayout
import it.neckar.lizergy.model.configuration.moduleLayout.ResolvedModuleLayout
import it.neckar.lizergy.model.configuration.moduleLayout.roof.ResolvedColoredMetalRoofTile
import it.neckar.lizergy.model.configuration.moduleLayout.roof.ResolvedColoredOnlyWhereVisibleMetalRoofTile
import it.neckar.lizergy.model.configuration.moduleLayout.roof.ResolvedNormalMetalRoofTile
import it.neckar.lizergy.model.configuration.quote.QuoteConfiguration
import it.neckar.lizergy.model.configuration.quote.builder.InverterType
import it.neckar.lizergy.model.configuration.quote.builder.Manufacturer
import it.neckar.lizergy.model.configuration.quote.builder.Wallbox
import it.neckar.lizergy.model.customer.AddressExample
import it.neckar.lizergy.model.customer.Customer
import it.neckar.lizergy.model.customer.Example
import it.neckar.lizergy.model.energy.Capacity
import it.neckar.lizergy.model.energy.PowerRating
import it.neckar.lizergy.model.energy.Voltage
import it.neckar.lizergy.model.financial.quote.ConfiguredOptionality
import it.neckar.lizergy.model.project.ProjectConfiguration.PhotovoltaicsProjectId
import it.neckar.lizergy.model.project.ResolvedProject
import it.neckar.lizergy.model.state.ProcessStateEntry
import it.neckar.lizergy.model.stumps.EinstellungWechselrichter
import it.neckar.open.formatting.format
import it.neckar.open.kotlin.lang.nullIfBlank
import it.neckar.open.kotlin.serializers.IsoMillis
import it.neckar.open.unit.si.cm
import it.neckar.open.unit.si.m
import it.neckar.open.unit.si.ms
import it.neckar.user.UserLoginName
import it.neckar.uuid.NativeUuid
import it.neckar.uuid.randomForEnvironment
import kotlinx.serialization.Serializable
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes
import kotlin.uuid.Uuid

@Serializable
data class AssemblyPortfolioForPDF(
  override val uuid: NativeUuid,
  override val projectId: PhotovoltaicsProjectId,
  val projektName: String,

  val sellingCompany: PlannerCompanyInformation,

  val auftragsDatum: IsoMillis?,
  val kundenberater: UserInformation,
  val ersteller: UserInformation?,
  val projektLeiterInformation: UserInformation?,
  val pvMontageDurchInformation: PlannerCompanyInformation,
  val ansprechpartnerDachInformation: UserInformation?,
  val elektroMontageDurchInformation: PlannerCompanyInformation,
  val ansprechpartnerElektroInformation: UserInformation?,

  val customer: Customer,

  override val locationFirstName: String,
  override val locationLastName: String,
  override val locationCompany: String?,
  val location: it.neckar.lizergy.model.customer.Address,
  override val locationPhone: String,
  override val locationCellphone: String,
  override val locationEmail: String,

  val facilityOperator1: FacilityOperatorInformation,
  val facilityOperator2: FacilityOperatorInformation,

  override val anlagenPasswort: String,
  override val einspeiseart: Einspeiseart,
  val einstellungWechselrichter: EinstellungWechselrichter,
  override val wartungsvertrag: Boolean,
  override val geruestVorhanden: String,
  val selfAssemblyConfiguration: SelfAssemblyConfiguration? = null,

  override val pvLeistung: PowerRating,
  override val leerlaufspannung: Voltage,
  override val verteilerNetzbetreiberName: String?,

  val kalkulierteStunden: Duration,
  override val ueberspannungsschutz: String,
  override val ueberspannungsschutzAC: String,
  val ueberspannungsschutzACAnzahl: Int,
  override val messkonzept: String,

  override val bleibenderZaehler: String,
  override val zusammenZuLegendeZaehlerNummern: List<String>,
  override val digitalerZaehler: Int?,

  override val slsEinbauen: Boolean,
  override val unterverteilerEinbauen: Boolean,
  override val erdspiessSetzen: Boolean,
  override val waermepumpenAnbindung: Boolean,
  override val internetAnschluss: Boolean,

  override val assemblyPortfolioModuleLayouts: List<Layout>,

  override val wechselrichter: StringSelection,
  override val numberOfStrings: Int,

  override val anzahlOptimierer: Int,
  override val optimierer: String,

  override val speicher: String,

  override val ladestationen: AssemblyPortfolio.CustomWallboxSelection<Ladestation>,
  override val heizstab: HeaterRod,

  override val ersatzstrom: String,
  override val sunnyHomeManagerOderMeter: String,

  override val firstEingespeist: Boolean,
  override val oberleitungZuIsolieren: Boolean,
  override val satAnlageZuVersetzen: Boolean,
  override val antenneEntfernen: Boolean,

  override val besonderheiten: String,
  override val bestandsanlagen: String,
  override val sonstigeArbeiten: String,

  ) : AssemblyPortfolio {

  override val projektLeiter: UserLoginName?
    get() = projektLeiterInformation?.loginName

  override val pvMontageDurch: CompanyCode
    get() = pvMontageDurchInformation.companyCode

  override val ansprechpartnerDach: UserLoginName?
    get() = ansprechpartnerDachInformation?.loginName

  override val elektroMontageDurch: CompanyCode
    get() = elektroMontageDurchInformation.companyCode

  override val ansprechpartnerElektro: UserLoginName?
    get() = ansprechpartnerElektroInformation?.loginName

  override val roofPdfDownloadedAt: PositionEditHistory<@ms Double?>
    get() = PositionEditHistory(null, null)

  override val basementPdfDownloadedAt: PositionEditHistory<@ms Double?>
    get() = PositionEditHistory(null, null)

  val locationFullName: String
    get() = "$locationFirstName $locationLastName"

  /**
   * The display name for the "Anlagenstandort":
   * [locationCompany] (manual override or [Customer.company]) when set,
   * otherwise [locationFullName].
   */
  val locationDisplayName: String
    get() = resolveLocationDisplayName(customer)

  val moduleSelection: StringSelection
    get() = StringSelection(assemblyPortfolioModuleLayouts.groupBy { it.module }
      .map { (module, layouts) -> ElementsSelectionEntry(module, layouts.sumOf { it.anzahlModule }) })

  val blechZiegelSelection: StringSelection
    get() = StringSelection(assemblyPortfolioModuleLayouts.groupBy { it.blechZiegel }
      .map { (blechZiegel, layouts) -> ElementsSelectionEntry(blechZiegel, layouts.sumOf { it.anzahlBlechZiegel ?: 0 }) })

  @Serializable
  data class Layout(
    override val id: ModuleLayout.ModuleLayoutId,
    override val name: String,
    override val anzahlModule: Int,
    override val module: String,
    override val montageSystem: String,
    override val montageSystemSuffix: String,
    override val anzahlBlechZiegel: Int?,
    override val blechZiegel: String?,
    override val moduleImKreuzverbund: Int,
    override val aufdachdaemmung: @cm Int?,
    override val verstaerkteSchienen: Boolean,
    override val schwarzeKlemmen: Boolean,
  ) : AssemblyPortfolioModuleLayout

  @Serializable
  data class Ladestation(
    override val id: Wallbox.WallboxId,
    override val name: String,
    override val leistung: Capacity,
    override val drosselung: Boolean,
    override val kabellaenge: @m Double,
    override val montageZeitInklusive: Boolean,
    override val montageZeit: Duration,
    override val kabelWeg: @m Double,
  ) : AssemblyPortfolioWallbox {
    fun format(): String {
      return buildString {
        append(name)
        append(" / ")
        append(leistung.format())
        if (drosselung) {
          append(" ")
          append("(Drosselung 11kW)")
        }
        append(" / ")
        append("${kabellaenge.format(maximumFractionDigits = 2)}m DC Kabel")
        append(" / ")
        append("${kabelWeg.format(maximumFractionDigits = 2)}m Kabelweg")
        //if (montageZeitInklusive) {
        //  append(" / ")
        //  append("Montagezeit in der AB")
        //}
        append(" / ")
        //append("Montagezeit ${montageZeit.formatHourAndMinutes()}")
        append("Rapport schreiben")
      }
    }
  }

  @Serializable
  data class HeaterRod(
    override val name: String,
    override val leistung: Capacity?,
    override val montageZeitInklusive: Boolean,
    override val montageZeit: Duration?,
  ) : AssemblyPortfolioHeaterRod {
    fun format(): String {
      if (name.isEmpty() || leistung == null) return ""
      return "$name / ${leistung.format()}${if (montageZeitInklusive) " / Montagezeit in der AB" else ""}"
    }
  }


  companion object {
    fun getMock(uuid: Uuid = Uuid.randomForEnvironment()): AssemblyPortfolioForPDF {
      return AssemblyPortfolioForPDF(
        uuid = uuid,
        projectId = PhotovoltaicsProjectId.random(),
        projektName = "Projektname",
        sellingCompany = PlannerCompanyInformation.NeckarIT,
        auftragsDatum = 0.0,
        kundenberater = UserInformation.NeckarIT,
        ersteller = UserInformation.NeckarIT,
        projektLeiterInformation = UserInformation.NeckarIT,
        pvMontageDurchInformation = PlannerCompanyInformation.NeckarIT,
        ansprechpartnerDachInformation = UserInformation.NeckarIT,
        elektroMontageDurchInformation = PlannerCompanyInformation.NeckarIT,
        ansprechpartnerElektroInformation = UserInformation.NeckarIT,
        customer = Customer.Example.LisaMüller,
        locationFirstName = "Max",
        locationLastName = "Mustermann",
        locationCompany = null,
        location = AddressExample.neckarIT,
        locationPhone = "012 3456 78",
        locationCellphone = "012 3456 78",
        locationEmail = "max@mustermann.de",
        facilityOperator1 = FacilityOperatorInformation(),
        facilityOperator2 = FacilityOperatorInformation(),
        anlagenPasswort = "Anlagen Passwort",
        einspeiseart = Einspeiseart.Ueberschuss,
        einstellungWechselrichter = EinstellungWechselrichter.FRE_RSE,
        wartungsvertrag = true,
        geruestVorhanden = "Ja, von FIRMA",
        selfAssemblyConfiguration = SelfAssemblyConfiguration(
          dachhakenSetzen = true,
          schienenMontage = true,
          kabelZiehenUndModuleMontieren = true,
          dachhakenGestellt = false,
        ),
        pvLeistung = PowerRating.wattPeak(12_400.0),
        leerlaufspannung = Voltage.voltage(60.0),
        verteilerNetzbetreiberName = "Muster Stadtwerke",
        kalkulierteStunden = 20.0.hours,
        ueberspannungsschutz = "Typ I & II",
        ueberspannungsschutzAC = "Überspannungsschutz AC",
        ueberspannungsschutzACAnzahl = 1,
        messkonzept = "Messkonzept",
        bleibenderZaehler = "Zähler 123456789",
        zusammenZuLegendeZaehlerNummern = listOf("123465789", "987654321"),
        digitalerZaehler = 1,
        slsEinbauen = true,
        unterverteilerEinbauen = true,
        erdspiessSetzen = true,
        waermepumpenAnbindung = true,
        internetAnschluss = true,
        assemblyPortfolioModuleLayouts = listOf(
          Layout(
            id = ModuleLayout.ModuleLayoutId(Uuid.parse("e1c51da9-4dc6-409f-82d3-5d5f3ce9dc09")),
            name = "Layout 1", montageSystem = "DaMontageSystem", montageSystemSuffix = "SUFF",
            anzahlModule = 30,
            module = "Modultyp",
            anzahlBlechZiegel = 16,
            blechZiegel = "Blaue Blechziegel",
            moduleImKreuzverbund = 7,
            aufdachdaemmung = 12,
            verstaerkteSchienen = true,
            schwarzeKlemmen = true,
          )
        ),
        wechselrichter = StringSelection(
          listOf(
            ElementsSelectionEntry("Wechselrichter 1", 2),
            ElementsSelectionEntry("Wechselrichter 2", 1)
          )
        ),
        numberOfStrings = 4,
        anzahlOptimierer = 3,
        optimierer = "Optimierer",
        speicher = "Batteriekasten 2000 / 4.4 SI",
        ladestationen = AssemblyPortfolio.CustomWallboxSelection(
          listOf(
            ElementsSelectionEntry(
              Ladestation(
                id = Wallbox.WallboxId(Uuid.parse("a69160d2-67da-4414-8bc4-6269205770cc")),
                name = "Ldestation A",
                leistung = Capacity.kW(11.0),
                drosselung = true,
                kabellaenge = 5.0,
                montageZeitInklusive = true,
                montageZeit = 90.minutes,
                kabelWeg = 3.0,
              ),
              1
            )
          )
        ),
        heizstab = HeaterRod("Heizstab 1", Capacity.kW(4.3), true, 25.minutes),
        ersatzstrom = "Ersatzstrom",
        sunnyHomeManagerOderMeter = "Home Manager OM",
        firstEingespeist = true,
        oberleitungZuIsolieren = true,
        satAnlageZuVersetzen = true,
        antenneEntfernen = true,
        besonderheiten = """
          Besonderheiten:
          - Besonderheit 1
          - Besonderheit 2
          - Besonderheit 3
        """.trimIndent(),
        bestandsanlagen = """
          Bestandsanlagen:
          - Bestandsanlage 1
          - Bestandsanlage 2
          - Bestandsanlage 3
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla nec purus euismod, fermentum nunc nec, ultricies nunc. Nullam nec nunc nec nunc.
        """.trimIndent(),
        sonstigeArbeiten = """
          Sonstige Arbeiten:
          - Sonstige Arbeit 1
          - Sonstige Arbeit 2
          - Sonstige Arbeit 3
        """.trimIndent(),
      )
    }
  }
}


fun ResolvedProject.createAssemblyPortfolioForPDF(
  forConfiguration: QuoteConfiguration,
  currentConfigurationProcessState: ProcessStateEntry?,
  currentAssemblyPortfolioProcessState: ProcessStateEntry?,
  userResolver: UserResolver,
  companyResolver: CompanyResolver,
): AssemblyPortfolioForPDF {
  val priceList = forConfiguration.priceList

  fun ResolvedModuleLayout.toPDFLayout(): AssemblyPortfolioForPDF.Layout {
    val moduleLayout = assemblyPortfolio.assemblyPortfolioModuleLayouts.firstOrNull { it.id == id }
    val anzahlBlechziegel = moduleLayout?.anzahlBlechZiegel ?: roof.metalRoofTileConstructionType?.amount
    val blechZiegel = moduleLayout?.blechZiegel ?: when (val metalRoofTileConstructionType = roof.metalRoofTileConstructionType) {
      ResolvedNormalMetalRoofTile, is ResolvedColoredMetalRoofTile -> metalRoofTileConstructionType.format(roof.roofTile?.metalRoofTile?.description)
      is ResolvedColoredOnlyWhereVisibleMetalRoofTile -> "${metalRoofTileConstructionType.formatWithoutAmount(roof.roofTile?.metalRoofTile?.description)} - ${anzahlBlechziegel?.let { metalRoofTileCount - it } ?: "Keine"} Blechziegel verzinkt"
      null -> null
    }
    return AssemblyPortfolioForPDF.Layout(
      id = id,
      name = moduleLayout?.name ?: layoutName,
      anzahlModule = moduleLayout?.anzahlModule ?: modulesCount,
      module = moduleLayout?.module ?: moduleType.description,
      montageSystem = moduleLayout?.montageSystem ?: roof.roofType.description,
      montageSystemSuffix = moduleLayout?.montageSystemSuffix.orEmpty(),
      anzahlBlechZiegel = anzahlBlechziegel ?: if (blechZiegel != null) metalRoofTileCount else null,
      blechZiegel = blechZiegel,
      moduleImKreuzverbund = moduleLayout?.moduleImKreuzverbund ?: horizontalModulesCount,
      aufdachdaemmung = moduleLayout?.aufdachdaemmung ?: roof.isolationThickness,
      verstaerkteSchienen = moduleLayout?.verstaerkteSchienen ?: roof.verstaerktesSchienensystem,
      schwarzeKlemmen = moduleLayout?.schwarzeKlemmen ?: roof.schwarzeKlemmen,
    )
  }

  fun ElementsSelectionEntry<InverterType>.toPDFInverter(index: Int): ElementsSelectionEntry<String> {
    val inverter = assemblyPortfolio.wechselrichter.entries.getOrNull(index)
    return ElementsSelectionEntry(inverter?.element ?: element.format(), inverter?.amount ?: amount)
  }

  fun ElementsSelectionEntry<Wallbox>.toPDFLadestation(): ElementsSelectionEntry<AssemblyPortfolioForPDF.Ladestation> {
    val relevantEntry = assemblyPortfolio.ladestationen.entries.firstOrNull { it.element.id == element.id }
    val relevantWallbox = relevantEntry?.element
    return ElementsSelectionEntry(
      AssemblyPortfolioForPDF.Ladestation(
        id = element.id,
        name = relevantWallbox?.name ?: element.description,
        leistung = relevantWallbox?.leistung ?: element.chargingCapacity.capacity(),
        drosselung = forConfiguration.wallboxSelection.throttled,
        kabellaenge = relevantWallbox?.kabellaenge ?: element.cableLength.length(),
        montageZeitInklusive = relevantWallbox?.montageZeitInklusive != false,
        montageZeit = relevantWallbox?.montageZeit ?: priceList.wallboxInstallationPrice.workingTime,
        kabelWeg = relevantWallbox?.kabelWeg ?: forConfiguration.wallboxSelection.kabelweg,
      ),
      relevantEntry?.amount ?: amount
    )
  }

  fun AssemblyPortfolio.HeaterRod.toPDFHeaterRod(): AssemblyPortfolioForPDF.HeaterRod {
    val heaterRod = forConfiguration.facilityConfiguration.heaterRod
    return AssemblyPortfolioForPDF.HeaterRod(
      name = name ?: heaterRod?.description.orEmpty(),
      leistung = leistung ?: heaterRod?.heatingCapacity?.capacity(),
      montageZeitInklusive = montageZeitInklusive,
      montageZeit = montageZeit ?: heaterRod?.let { priceList[heaterRod].workingTime },
    )
  }

  val sunnyHomeManagerOderMeter = buildList {
    val batteryConfiguration = forConfiguration.facilityConfiguration.batteryConfiguration
    if (batteryConfiguration != null || forConfiguration.electricityWorkConfiguration.sunnyHomeManager) {
      val relevantManufacturer = batteryConfiguration?.inverterType?.manufacturer ?: forConfiguration.facilityConfiguration.inverterSelectionWithBatteryInverter.firstOrNull()?.element?.manufacturer
      when (relevantManufacturer) {
        Manufacturer.SMA -> add("Sunny Home Manager 2.0")
        Manufacturer.IBC -> add("IBC HomeOne Hub BT-RE1")
        null -> {}
        else -> throw IllegalStateException("Unknown manufacturer for inverterType: $relevantManufacturer")
      }
    }
    if (forConfiguration.electricityWorkConfiguration.smaEnergyMeter) add("SMA-E-Meter")
  }.joinToString(" + ").nullIfBlank()

  val ueberspannungsSchutzType = buildString {
    append("Überspannungsschutz DC ")
    if (forConfiguration.assemblyConfiguration.externerBlitzschutzVorhanden) {
      append("Typ 1 & ")
    }
    append("Typ 2")
    if (forConfiguration.assemblyConfiguration.externerBlitzschutzVorhanden) {
      append(" [16mm²]")
    } else {
      append(" [6mm²]")
    }
  }

  val ueberspannungsschutzACType = forConfiguration.electricityWorkConfiguration.ueberspannungsschutzAC
  val ueberspannungsschutzACDefault = ueberspannungsschutzACType.format()

  return AssemblyPortfolioForPDF(
    uuid = uuid,
    projectId = projectId,
    projektName = forConfiguration.configurationName,
    sellingCompany = companyResolver[forConfiguration.sellingCompany],
    auftragsDatum = currentConfigurationProcessState?.assignedAt,
    kundenberater = maintainerInformation,
    ersteller = currentAssemblyPortfolioProcessState?.assignedTo?.let { userResolver[it] },
    projektLeiterInformation = assemblyPortfolio.projektLeiterInformation,
    pvMontageDurchInformation = assemblyPortfolio.pvMontageDurchInformation ?: companyResolver[forConfiguration.earningsDistribution.montageDach.company],
    ansprechpartnerDachInformation = assemblyPortfolio.ansprechpartnerDachInformation,
    elektroMontageDurchInformation = assemblyPortfolio.elektroMontageDurchInformation ?: companyResolver[forConfiguration.earningsDistribution.elektroInstallation.company],
    ansprechpartnerElektroInformation = assemblyPortfolio.ansprechpartnerElektroInformation,
    customer = customer,
    locationFirstName = assemblyPortfolio.locationFirstName ?: customer.firstName,
    locationLastName = assemblyPortfolio.locationLastName ?: customer.lastName,
    locationCompany = assemblyPortfolio.locationCompany ?: customer.company,
    location = forConfiguration.location.address,
    locationPhone = assemblyPortfolio.locationPhone ?: customer.phone.orEmpty(),
    locationCellphone = assemblyPortfolio.locationCellphone ?: customer.cellphone.orEmpty(),
    locationEmail = assemblyPortfolio.locationEmail ?: customer.email.orEmpty(),
    facilityOperator1 = forConfiguration.facilityOperator1,
    facilityOperator2 = forConfiguration.facilityOperator2,
    anlagenPasswort = assemblyPortfolio.anlagenPasswort ?: "",
    einspeiseart = assemblyPortfolio.einspeiseart ?: forConfiguration.einspeiseart,
    einstellungWechselrichter = gridAssessment.einstellungWechselrichter,
    wartungsvertrag = assemblyPortfolio.wartungsvertrag ?: (forConfiguration.facilityConfiguration.onlineMonitoring == ConfiguredOptionality.Selected),
    geruestVorhanden = assemblyPortfolio.geruestVorhanden ?: forConfiguration.formatGeruest(companyResolver),
    selfAssemblyConfiguration = forConfiguration.assemblyConfiguration.assemblyStatus as? SelfAssemblyConfiguration,
    pvLeistung = assemblyPortfolio.pvLeistung ?: forConfiguration.kiloWattPeak,
    leerlaufspannung = assemblyPortfolio.leerlaufspannung ?: forConfiguration.moduleLayouts.totalOffLoadVoltage,
    verteilerNetzbetreiberName = gridAssessment.verteilerNetzbetreiberName,
    kalkulierteStunden = forConfiguration.assemblyWorkingTime,
    ueberspannungsschutz = assemblyPortfolio.ueberspannungsschutz ?: ueberspannungsSchutzType,
    ueberspannungsschutzAC = assemblyPortfolio.ueberspannungsschutzAC ?: ueberspannungsschutzACDefault,
    ueberspannungsschutzACAnzahl = ueberspannungsschutzACType.anzahl,
    messkonzept = (assemblyPortfolio.messkonzeptOverride ?: assemblyPortfolio.messkonzept).orEmpty(),
    bleibenderZaehler = assemblyPortfolio.bleibenderZaehler ?: forConfiguration.zaehlerNummer,
    zusammenZuLegendeZaehlerNummern = assemblyPortfolio.zusammenZuLegendeZaehlerNummern ?: forConfiguration.electricityWorkConfiguration.zusammenZuLegendeZaehlerNummern,
    digitalerZaehler = assemblyPortfolio.digitalerZaehler ?: forConfiguration.electricityWorkConfiguration.einbauDigitalerZaehler,
    slsEinbauen = assemblyPortfolio.slsEinbauen ?: forConfiguration.electricityWorkConfiguration.slsNachruesten,
    unterverteilerEinbauen = assemblyPortfolio.unterverteilerEinbauen ?: forConfiguration.electricityWorkConfiguration.einbauUnterverteiler,
    erdspiessSetzen = assemblyPortfolio.erdspiessSetzen ?: forConfiguration.facilityConfiguration.erdspiessSetzen,
    waermepumpenAnbindung = assemblyPortfolio.waermepumpenAnbindung ?: forConfiguration.facilityConfiguration.waermepumpenanbindung,
    internetAnschluss = assemblyPortfolio.internetAnschluss ?: (forConfiguration.facilityConfiguration.integrateInverterIntoNetwork == ConfiguredOptionality.Selected),
    assemblyPortfolioModuleLayouts = forConfiguration.moduleLayouts.validElements.map { it.toPDFLayout() },
    wechselrichter = StringSelection(forConfiguration.facilityConfiguration.inverterSelectionWithBatteryInverter.mapIndexed { index, entry -> entry.toPDFInverter(index) }),
    numberOfStrings = assemblyPortfolio.numberOfStrings ?: forConfiguration.facilityConfiguration.numberOfStrings,
    anzahlOptimierer = assemblyPortfolio.anzahlOptimierer ?: forConfiguration.facilityConfiguration.numberOfOptimizers,
    optimierer = assemblyPortfolio.optimierer.orEmpty(),
    speicher = assemblyPortfolio.speicher ?: forConfiguration.facilityConfiguration.batteryConfiguration?.formatOnlyBattery().orEmpty(),
    ladestationen = AssemblyPortfolio.CustomWallboxSelection(forConfiguration.wallboxSelection.entriesNonEmpty.map { it.toPDFLadestation() }),
    heizstab = assemblyPortfolio.heizstab.toPDFHeaterRod(),
    ersatzstrom = assemblyPortfolio.ersatzstrom ?: forConfiguration.electricityWorkConfiguration.independenceManager?.assemblyPortfolioDescription.orEmpty(),
    sunnyHomeManagerOderMeter = assemblyPortfolio.sunnyHomeManagerOderMeter ?: sunnyHomeManagerOderMeter ?: "Nichts",
    firstEingespeist = assemblyPortfolio.firstEingespeist,
    oberleitungZuIsolieren = assemblyPortfolio.oberleitungZuIsolieren ?: forConfiguration.assemblyConfiguration.dachstaenderIsolierung,
    satAnlageZuVersetzen = assemblyPortfolio.satAnlageZuVersetzen ?: forConfiguration.assemblyConfiguration.moveSatelliteDish,
    antenneEntfernen = assemblyPortfolio.antenneEntfernen ?: forConfiguration.assemblyConfiguration.removeAntenna,
    besonderheiten = assemblyPortfolio.besonderheiten ?: forConfiguration.besonderheitenForAssemblyPortfolio,
    bestandsanlagen = assemblyPortfolio.bestandsanlagen ?: forConfiguration.bestandsanlagesForAssemblyPortfolio,
    sonstigeArbeiten = assemblyPortfolio.sonstigeArbeiten ?: forConfiguration.sonstigesForAssemblyPortfolio,
  )
}

fun QuoteConfiguration.formatGeruest(companyResolver: CompanyResolver): String {
  return when (val assemblyStatus = assemblyConfiguration.assemblyStatus) {
    is ScaffoldingArea -> assemblyStatus.scaffoldingArea.let { if (it != null && it > 0) "$it m²" else "Nein" }
    ScaffoldingProvided -> "Ja, durch ${companyResolver[earningsDistribution.montageGeruest.company].name}"
    is SelfAssemblyConfiguration -> "Ja, bauseits"
  }
}
